<template>
  <BModal
    :model-value="modal"
    scrollable
    centered
    @update:modelValue="updateModal"
  >
    <template #header>
      <div
        class="d-flex align-items-center justify-content-between modal-title w-100"
      >
        <h5 class="">Admin</h5>
      </div>
      <BButton variant="link" class="close" @click="closeModal">
        <i class="ion ion-android-close"></i>
      </BButton>
    </template>

    <BContainer>
      <BRow>
        <BCol>
          <BFormSelect
            v-model="adminForm.employee_uuid"
            :options="staffOptions"
            class="form-control mb-3"
            :disabled="adminData.uuid"
          >
            <template #first>
              <BFormSelectOption :value="null" disabled
                >Select Staff</BFormSelectOption
              >
            </template>
          </BFormSelect>
        </BCol>
      </BRow>
      <BRow>
        <BCol>
          <BFormInput
            v-model="adminForm.username"
            placeholder="Username"
            class="form-group"
            type="text"
          />
        </BCol>
      </BRow>
      <BRow v-if="adminData.uuid">
        <BCol>
          <BFormInput
            v-model="adminForm.email"
            placeholder="Email"
            class="form-group"
            type="text"
            :disabled="adminData.uuid"
          />
        </BCol>
      </BRow>
      <BRow>
        <BCol>
          <BFormInput
            v-model="adminForm.password"
            placeholder="Password"
            class="form-group"
            type="password"
          />
        </BCol>
      </BRow>
      <BRow>
        <BCol>
          <BFormInput
            v-model="adminForm.password_confirmation"
            placeholder="Confirm Password"
            class="form-group"
            type="password"
          />
        </BCol>
      </BRow>
      <BRow>
        <BCol>
          <BFormSelect
            v-model="adminForm.role"
            :options="roleOptions"
            placeholder="Select Role"
            class="form-control"
          >
            <template #first>
              <BFormSelectOption :value="null" disabled>
                Select Position
              </BFormSelectOption>
            </template>
          </BFormSelect>
        </BCol>
      </BRow>
      <BRow v-if="adminData.uuid" class="mt-3">
        <BCol>
          <BFormSelect
            v-model="adminForm.status"
            :options="statusOptions"
            placeholder="Select Status"
            class="form-control"
          />
        </BCol>
      </BRow>
    </BContainer>

    <template #footer>
      <BButton variant="primary" @click="handleAction">
        {{ adminData && adminData.uuid ? "Update" : "Save" }}
      </BButton>
      <BButton variant="secondary" @click="closeModal">Cancel</BButton>
    </template>
  </BModal>

  <ToastResponse
    v-model="modalResponse.toast"
    :text="modalResponse.text"
    :success="modalResponse.success"
  />
</template>

<script>
import {
  BModal,
  BRow,
  BCol,
  BFormInput,
  BFormSelect,
  BFormSelectOption,
  BButton,
  BContainer,
} from "bootstrap-vue-next";
import { ref, inject, watch } from "vue";
import ToastResponse from "@/components/ToastResponse.vue";

export default {
  name: "AdminForm",
  props: {
    modal: Boolean,
    adminData: Object,
  },
  components: {
    BModal,
    BRow,
    BCol,
    BFormInput,
    BFormSelect,
    BFormSelectOption,
    BButton,
    BContainer,
    ToastResponse,
  },
  emits: ["update:modal", "addOrUpdate"],
  setup(props, { emit }) {
    const repositories = inject("repositories");
    const staffRepository = repositories?.employee || null;
    const adminRepository = repositories?.admin || null;
    // const positionRepository = repositories?.staffPosition || null;

    const staffOptions = ref([]);
    // const positionOptions = ref([]);

    const adminForm = ref({
      employee_uuid: null,
      username: "",
      password: "",
      password_confirmation: "",
      role: null,
      status: "",
    });
    const confirmPassword = ref("");

    const roleOptions = ref([
      { value: "superadmin", text: "Superadmin" },
      { value: "backoffice", text: "Backoffice" },
      { value: "supervisor", text: "Supervisor" },
      { value: "receptionist", text: "Receptionist" },
      { value: "housekeeping", text: "Housekeeping" },
      { value: "maintenance", text: "Maintenance" },
    ]);

    const statusOptions = [
      { value: "active", text: "Active" },
      { value: "inactive", text: "Inactive" },
    ];

    const modalResponse = ref({
      toast: false,
      text: "",
      success: false,
    });

    const updateModal = (value) => {
      emit("update:modal", value);
    };

    const closeModal = () => {
      updateModal(false);
      resetForm();
    };

    const resetForm = () => {
      adminForm.value = {
        employee_uuid: null,
        username: "",
        password: "",
        password_confirmation: "",
        role: null,
        status: "active",
      };
      confirmPassword.value = "";
    };

    const handleAction = async () => {
      props.adminData?.uuid ? update() : create();
    };

    const create = async () => {
      // adminForm.value.password_confirmation = adminForm.value.password;

      await adminRepository
        .create(adminForm.value)
        .then(() => {
          updateModal(false);
          modalResponse.value = {
            toast: true,
            text: "Staff created successfully",
            success: true,
          };
          emit("addOrUpdate");
          resetForm();
        })
        .catch((err) => {
          console.log("error bang", err);
          Object.assign(modalResponse.value, {
            success: false,
            text: err.response.data.error,
            toast: true,
          });
        });
    };

    const update = async () => {
      // adminForm.value.password_confirmation = confirmPassword.value;

      await adminRepository
        .update(adminForm.value, props.adminData.uuid)
        .then(() => {
          updateModal(false);
          modalResponse.value = {
            toast: true,
            text: "Admin updated successfully",
            success: true,
          };
          emit("addOrUpdate");
          resetForm();
        })
        .catch((err) => {
          console.log("error bang", err);
          Object.assign(modalResponse.value, {
            success: false,
            text: err.response.data.error,
            toast: true,
          });
        });
    };

    watch(
      () => props.modal,
      async (newVal) => {
        if (newVal) {
          const staffRes = await staffRepository.getAll();
          const adminRes = await adminRepository.getAll();

          const adminUuids = adminRes.data.data.map(
            (admin) => admin.employee_uuid || admin.employee_uuid
          );
          const staffUuids = staffRes.data.data.filter(
            (staff) =>
              staff.status === "active" && !adminUuids.includes(staff.uuid)
          );

          if (props.adminData && props.adminData.employee_uuid) {
            const currentStaff = staffRes.data.data.find(
              (staff) => staff.uuid === props.adminData.employee_uuid
            );
            if (currentStaff && !staffUuids.includes(currentStaff)) {
              staffUuids.unshift(currentStaff);
            }
          }

          staffOptions.value = staffUuids.map((item) => ({
            value: item.uuid,
            text: item.fullname,
          }));

          if (props.adminData) {
            adminForm.value = {
              employee_uuid:
                props.adminData.employee_uuid ||
                props.adminData.employee?.uuid ||
                null,
              username: props.adminData.username || "",
              email: props.adminData.email || "",
              password: "",
              role: props.adminData.role || null,
              status: props.adminData.status || "active",
            };
            confirmPassword.value = "";
          } else {
            resetForm();
          }
        }
      },
      { immediate: true }
    );

    return {
      adminForm,
      confirmPassword,
      staffOptions,
      // positionOptions,
      roleOptions,
      statusOptions,
      handleAction,
      updateModal,
      closeModal,
      modalResponse,
    };
  },
};
</script>
